/**
 * G6.1 — Command bar: ONE dark strip pinned (sticky) above the dispatch
 * board. Everything the old header row + title block scattered around is
 * relocated here:
 *
 *   • date nav (‹ date › Today) — the timezone note that used to be a
 *     paragraph under the page title now lives on the date pill's tooltip
 *   • Day/Week toggle
 *   • trades filter
 *   • Views ▾ (Map, Route planner, Crew monitor, Full-day, page density)
 *   • compact counters — the ONE scoreboard (#761): ready for dispatch /
 *     scheduled today / in progress / needs attention / completed / tech
 *     capacity. The live-pulse chips that used to be their own strip fold
 *     in here (in progress + completed render only when non-zero). Every
 *     pill carries a Radix tooltip defining its term; the attention pill's
 *     tooltip also previews the server's per-job reasons, and clicking it
 *     opens the triage bar's expanded detail (onAttentionClick).
 *   • the Automation cluster (auto-schedule master toggle + Auto-fill +
 *     Suggest moves)
 *
 * AUTOMATION PRIVACY (hard rule, §8): the Automation cluster renders ONLY
 * when the signed-in user holds `dispatch.manage` (canAutomate) — no
 * automation UI, labels, or hints for anyone else. The permission arrives
 * through the /me payload (`useAuth().can`), same source the shell already
 * used for the master toggle.
 *
 * Visual language: the bar is chrome, not page content — it sits on the
 * dark shell (bg-shell + text-shell-foreground per AGENTS.md), matching
 * the sticky strips on the Money page and field workflows.
 */
import { type ComponentProps, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
    Activity,
    CalendarDays,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock,
    Map as MapIcon,
    Maximize2,
    PanelsTopLeft,
    Route as RouteIcon,
    Scale,
    Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LiveChip, type LiveChipProps } from "./live-chip";
import { OutTodayChip } from "./out-today-chip";
import { useUiDensity, type UiDensity } from "@/lib/use-ui-density";
import { useBoardDensity, type BoardDensity } from "@/lib/use-board-density";
import { addDaysToDateKey, appDisplayTimeZone, localDateKey, weekdayFromDateKey } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import { AutoScheduleToggle } from "./auto-schedule-toggle";
import type { Trade } from "./dispatch-types";

/**
 * Every control inside the bar pins its OWN dark background + light text.
 * The theme-aware Button `outline`/`ghost` variants and the shared Select
 * set `bg-card`/`bg-background` but NO text color, so inside the bar's
 * `text-shell-foreground` context the label inherited WHITE — white-on-white
 * blank pills in the light theme (text only appeared on hover, when
 * `hover:text-secondary-foreground` finally pinned a color). These classes
 * are theme-independent: the shell tokens are a dark strip + light text in
 * BOTH themes, so nothing can flip underneath the controls.
 */
const DARK_CTRL =
    "border-shell-foreground/25 bg-shell-foreground/10 text-shell-foreground " +
    "hover:bg-shell-foreground/20 hover:text-shell-foreground active:bg-shell-foreground/25";

export interface CommandBarCounters {
    /** Jobs awaiting dispatch (no tech or no time). NOT date-scoped — the
     *  same backlog on every date, and labeled as such (#826: this pill
     *  used to be called "Ready", which reads as a readiness verdict). */
    ready: number;
    /** Jobs scheduled on the selected day. */
    scheduledToday: number;
    /** Techs on site right now, from the live pulse. */
    inProgress: number;
    /** Date-scoped attention (#826): on today, at-risk + overdue from the
     *  live pulse; on a future date, that day's not-ready jobs from
     *  /api/dispatch/readiness (missing summary/contact/devices/provider/
     *  parts/unconfirmed). Never a live-pulse zero wearing a date-agnostic
     *  label. */
    needsAttention: number;
    /** Jobs finished on the selected day, from the live pulse. */
    completed: number;
    /** Techs with at least one job today. */
    techsOnDuty: number;
    techsTotal: number;
}

export interface CommandBarProps {
    selectedDate: string;
    onSelectDate: (dateKey: string) => void;
    mode: "day" | "week";
    onModeChange: (mode: "day" | "week") => void;
    trades: Trade[];
    tradeFilter: string;
    onTradeFilterChange: (tradeId: string) => void;
    fullDayView: boolean;
    onToggleFullDay: () => void;
    /** #975 "Fit day": reframe the day board to the work actually scheduled
     *  and squeeze it into view. Opt-in — never changes where the board opens
     *  until a dispatcher asks for it. */
    fitDay: boolean;
    onToggleFitDay: () => void;
    counters: CommandBarCounters;
    /** Whether the selected date is today — drives which meaning the
     *  Attention pill carries (live pulse vs day readiness) and keeps the
     *  In progress / Done pills rendered at 0 on future days instead of
     *  vanishing (the header must keep its shape between dates, #826). */
    isToday: boolean;
    /** Top per-job reasons behind the attention count ("Title — note"),
     *  previewed in the attention pill's tooltip. */
    attentionNotes?: string[];
    /** Opens the triage bar's expanded state (live pulse detail). */
    onAttentionClick: () => void;
    /** AUTOMATION PRIVACY: true only for dispatch.manage holders. */
    canAutomate: boolean;
    autoSchedule: ComponentProps<typeof AutoScheduleToggle>;
    onAutoFill: () => void;
    onSuggestMoves: () => void;
    /** #1465 — the live light. Omitted only in tests that predate it. */
    live?: LiveChipProps;
    /** #1766 — the per-person "Try the new Grid" switch. Omitted in tests
     *  that predate it. */
    newGrid?: { on: boolean; onChange: (on: boolean) => void };
    /** #1766 — the new Grid's day view: the six counters are replaced by the
     *  day strip's colored bar, so the bar here drops them. */
    quiet?: boolean;
    /** The "out today" chip (call-ins + time off). Omitted in tests that predate it. */
    showOutToday?: boolean;
}

/** The old/new Grid switch, styled for the dark command bar. */
function NewGridSwitch({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            data-testid="new-grid-switch"
            onClick={() => onChange(!on)}
            title={
                on
                    ? "You're on the new Grid. Only your view changed — same jobs, same actions. Click to go back to the classic Grid."
                    : "Try the new Grid: color says each job's state, details are a hover away. Only your view changes."
            }
            className="inline-flex items-center gap-2 rounded-full px-2 py-1 text-xs font-semibold text-shell-foreground transition-colors hover:bg-shell-foreground/10"
        >
            <span
                aria-hidden
                className={cn(
                    "relative h-5 w-9 shrink-0 rounded-full transition-colors",
                    on ? "bg-primary" : "bg-shell-foreground/30",
                )}
            >
                <span
                    className={cn(
                        "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
                        on ? "left-[18px]" : "left-0.5",
                    )}
                />
            </span>
            {on ? "New Grid" : "Try the new Grid"}
        </button>
    );
}

/**
 * One scoreboard pill. #761: the native `title` hovers are gone — every
 * pill wraps a Radix tooltip (keyboard-focusable too) so the definition of
 * the term is one hover away, matching the audit ask "nothing on the page
 * defines at risk vs attention vs overdue".
 */
function CounterPill({
    label,
    value,
    dotClass,
    onClick,
    tip,
}: {
    label: string;
    value: number | string;
    dotClass: string;
    onClick?: () => void;
    tip: ReactNode;
}) {
    const inner = (
        <>
            <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
            <span className="text-sm font-bold leading-none tabular-nums">{value}</span>
            <span className="text-[10px] font-medium uppercase leading-none tracking-wide text-shell-foreground/70">
                {label}
            </span>
        </>
    );
    const core = onClick ? (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center gap-1.5 rounded-md px-2 py-1.5 transition-colors hover:bg-shell-foreground/10"
        >
            {inner}
        </button>
    ) : (
        <div tabIndex={0} className="flex items-center gap-1.5 rounded-md px-2 py-1.5">
            {inner}
        </div>
    );
    return (
        <Tooltip>
            <TooltipTrigger asChild>{core}</TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
                {tip}
            </TooltipContent>
        </Tooltip>
    );
}

export function CommandBar({
    selectedDate,
    onSelectDate,
    mode,
    onModeChange,
    trades,
    tradeFilter,
    onTradeFilterChange,
    fullDayView,
    onToggleFullDay,
    fitDay,
    onToggleFitDay,
    counters,
    isToday,
    attentionNotes = [],
    onAttentionClick,
    canAutomate,
    autoSchedule,
    onAutoFill,
    onSuggestMoves,
    live,
    newGrid,
    quiet = false,
    showOutToday = false,
}: CommandBarProps) {
    const navigate = useNavigate();
    const { density, setDensity } = useUiDensity();
    const { density: boardDensity, setDensity: setBoardDensity } = useBoardDensity();

    const timezoneNote = appDisplayTimeZone()
        ? `Times shown in ${appDisplayTimeZone()} (IANA).`
        : `Times shown in your browser zone (${Intl.DateTimeFormat().resolvedOptions().timeZone}).`;

    return (
        <div
            data-testid="dispatch-command-bar"
            className="sticky top-0 z-30 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-shell-foreground/15 bg-shell/95 px-2.5 py-2 text-shell-foreground shadow-md backdrop-blur"
        >
            <span className="hidden text-[10px] font-black uppercase tracking-[0.2em] text-shell-foreground/50 2xl:inline">
                The Grid
            </span>

            {/* ── Date nav ─────────────────────────────────────────── */}
            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="icon"
                    className={cn("h-8 w-8", DARK_CTRL)}
                    title="Previous day"
                    onClick={() => onSelectDate(addDaysToDateKey(selectedDate, -1))}
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                {/* One pill: weekday label + native date input. [color-scheme:dark]
                    keeps the input's own text + calendar glyph light on the dark
                    pill in both themes. */}
                <label
                    title={timezoneNote}
                    className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-shell-foreground/25 bg-shell-foreground/10 px-2 text-sm text-shell-foreground shadow-sm transition-all duration-150 focus-within:border-primary focus-within:ring-2 focus-within:ring-ring/30"
                >
                    <CalendarDays className="h-4 w-4 text-shell-foreground/80" />
                    <span className="whitespace-nowrap font-semibold">{weekdayFromDateKey(selectedDate)}</span>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => onSelectDate(e.target.value)}
                        className="bg-transparent text-sm text-shell-foreground [color-scheme:dark] focus-visible:outline-none"
                    />
                </label>
                <Button
                    variant="outline"
                    size="icon"
                    className={cn("h-8 w-8", DARK_CTRL)}
                    title="Next day"
                    onClick={() => onSelectDate(addDaysToDateKey(selectedDate, 1))}
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn("h-8", DARK_CTRL)}
                    onClick={() => onSelectDate(localDateKey(new Date()))}
                >
                    Today
                </Button>
            </div>

            {/* ── Out today — who called in or is off this day. The call-in
                alert's one home: not the bell, no push. ─────────────── */}
            {showOutToday && <OutTodayChip selectedDate={selectedDate} isToday={isToday} onJumpToDate={onSelectDate} />}

            {/* ── Day / Week ───────────────────────────────────────── */}
            <div className="inline-flex rounded-md border border-shell-foreground/25 bg-shell-foreground/10 p-0.5">
                <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                        "h-7",
                        mode === "day"
                            ? "bg-shell-foreground/25 text-shell-foreground hover:bg-shell-foreground/30 hover:text-shell-foreground"
                            : "text-shell-foreground/70 hover:bg-shell-foreground/15 hover:text-shell-foreground",
                    )}
                    onClick={() => onModeChange("day")}
                >
                    Day
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                        "h-7",
                        mode === "week"
                            ? "bg-shell-foreground/25 text-shell-foreground hover:bg-shell-foreground/30 hover:text-shell-foreground"
                            : "text-shell-foreground/70 hover:bg-shell-foreground/15 hover:text-shell-foreground",
                    )}
                    onClick={() => onModeChange("week")}
                    data-teach="grid.week"
                >
                    Week
                </Button>
            </div>

            {/* ── Card density (day board only) ────────────────────────
                #587: the widescreen benefit lever. Comfortable = readable
                cards, the small-screen-friendly default; Compact = a denser
                whole-day overview for big monitors. Sizes the swimlane via
                the shared useBoardDensity hook. */}
            {mode === "day" && (
                <div
                    className="hidden items-center rounded-md border border-shell-foreground/25 bg-shell-foreground/10 p-0.5 sm:inline-flex"
                    title="Card density — Comfortable is readable on any screen; Compact fits more of the day on wide monitors"
                >
                    {(["comfortable", "compact"] as BoardDensity[]).map((d) => (
                        <Button
                            key={d}
                            variant="ghost"
                            size="sm"
                            className={cn(
                                "h-7 capitalize",
                                boardDensity === d
                                    ? "bg-shell-foreground/25 text-shell-foreground hover:bg-shell-foreground/30 hover:text-shell-foreground"
                                    : "text-shell-foreground/70 hover:bg-shell-foreground/15 hover:text-shell-foreground",
                            )}
                            onClick={() => setBoardDensity(d)}
                        >
                            {d}
                        </Button>
                    ))}
                </div>
            )}

            {/* ── Fit day (day board only) ─────────────────────────────
                #975: squeeze the day's actual work span into the viewport, so
                a tech whose stops sit past the right edge stops reading as
                idle. It cannot always fit completely — card widths have their
                own floor, so this compresses the clock, not the cards — which
                is why the lanes keep their off-screen ghosts either way. */}
            {mode === "day" && (
                <Button
                    variant="ghost"
                    size="sm"
                    aria-pressed={fitDay}
                    onClick={onToggleFitDay}
                    data-teach="grid.fit-day"
                    title="Fit day — reframe the board to the hours that actually have work, and squeeze them into view. Stops that still don't fit keep their off-screen marker."
                    className={cn(
                        "hidden h-8 sm:inline-flex",
                        fitDay
                            ? "bg-shell-foreground/25 text-shell-foreground hover:bg-shell-foreground/30 hover:text-shell-foreground"
                            : "text-shell-foreground/70 hover:bg-shell-foreground/15 hover:text-shell-foreground",
                    )}
                >
                    <Maximize2 className="mr-1.5 h-4 w-4" /> Fit day
                </Button>
            )}

            {/* ── Trades filter ────────────────────────────────────── */}
            <Select
                value={tradeFilter}
                onChange={(e) => onTradeFilterChange(e.target.value)}
                className="h-8 w-36 border-shell-foreground/25 bg-shell-foreground/10 text-shell-foreground [color-scheme:dark]"
                aria-label="Filter by trade"
            >
                <option value="">All trades</option>
                {trades.map((t) => (
                    <option key={t.id} value={t.id}>
                        {t.name}
                    </option>
                ))}
            </Select>

            {/* ── Views ▾ — relocated view controls ────────────────── */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    {/* #828 M11: the bare word "Views" hid three real screens —
                        name what's inside so they're discoverable. */}
                    <Button
                        variant="outline"
                        size="sm"
                        className={cn("h-8", DARK_CTRL)}
                        title="Other ways to see this day: Map, Route planner, Crew monitor — plus layout options"
                        data-teach="grid.views"
                    >
                        <PanelsTopLeft className="mr-1 h-4 w-4" /> Views
                        <span className="ml-1 hidden text-[10px] font-normal opacity-60 lg:inline">
                            map · routes · crew
                        </span>
                        <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onSelect={() => navigate("/dispatch/map")}>
                        <MapIcon className="mr-2 h-4 w-4" /> Map
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate("/dispatch/route-planner")}>
                        <RouteIcon className="mr-2 h-4 w-4" /> Route planner
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate("/ops")}>
                        <Activity className="mr-2 h-4 w-4" /> Crew monitor
                    </DropdownMenuItem>
                    {mode === "day" && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={onToggleFullDay}>
                                <Clock className="mr-2 h-4 w-4" /> Full day (24h)
                                {fullDayView && <Check className="ml-auto h-4 w-4" />}
                            </DropdownMenuItem>
                        </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-foreground/60">Page density</DropdownMenuLabel>
                    {(["standard", "wide", "full"] as UiDensity[]).map((d) => (
                        <DropdownMenuItem key={d} onSelect={() => setDensity(d)}>
                            <span className="capitalize">{d}</span>
                            {density === d && <Check className="ml-auto h-4 w-4" />}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* ── Compact counters — the one scoreboard (#761) ─────── */}
            {quiet ? (
                <div className="ml-auto" />
            ) : (
                <TooltipProvider delayDuration={200}>
                    <div className="ml-auto flex items-center">
                        {/* #826: this pill was "READY" — first, biggest, and the
                        most misleading word on the board (it's the unscheduled
                        backlog, identical on every date). Named for what it is,
                        and the tooltip says so. */}
                        <CounterPill
                            label="Awaiting dispatch"
                            value={counters.ready}
                            dotClass="bg-amber-400"
                            tip="The backlog: jobs still needing a tech or a time. Same pile on every date — not a readiness verdict for this day."
                        />
                        <CounterPill
                            label="Scheduled"
                            value={counters.scheduledToday}
                            dotClass="bg-sky-400"
                            tip="Jobs scheduled on the selected day."
                        />
                        {/* In progress / Done render at 0 on other days instead of
                        vanishing — the header keeps its shape between dates. */}
                        {mode === "day" && (
                            <CounterPill
                                label="In progress"
                                value={isToday ? counters.inProgress : 0}
                                dotClass="bg-blue-400"
                                tip={
                                    isToday
                                        ? "Techs on site right now, from the live pulse."
                                        : "Nothing runs on a day that hasn't started."
                                }
                            />
                        )}
                        {mode === "day" && (
                            <CounterPill
                                label="Attention"
                                value={counters.needsAttention}
                                dotClass={counters.needsAttention > 0 ? "bg-red-500" : "bg-emerald-400"}
                                onClick={onAttentionClick}
                                tip={
                                    <div className="space-y-1">
                                        {isToday ? (
                                            <p>
                                                At risk = running long or a late start. Overdue = past its scheduled
                                                end, never started. Missed = never ran on its day and needs a new slot.
                                            </p>
                                        ) : (
                                            <p>
                                                This day&apos;s jobs that aren&apos;t ready — missing a summary, an
                                                on-site contact, devices, a water provider, staged parts, or an
                                                unconfirmed dispatch.
                                            </p>
                                        )}
                                        {attentionNotes.length > 0 && (
                                            <ul className="list-disc space-y-0.5 pl-4">
                                                {attentionNotes.map((n) => (
                                                    <li key={n}>{n}</li>
                                                ))}
                                            </ul>
                                        )}
                                        <p className="text-white/70">Click for the full list.</p>
                                    </div>
                                }
                            />
                        )}
                        {mode === "day" && (
                            <CounterPill
                                label="Done"
                                value={isToday ? counters.completed : 0}
                                dotClass="bg-emerald-400"
                                tip={
                                    isToday
                                        ? "Jobs finished on the selected day."
                                        : "Nothing is done on a day that hasn't started."
                                }
                            />
                        )}
                        {counters.techsOnDuty > 0 && (
                            <CounterPill
                                label="Capacity"
                                value={`${counters.techsOnDuty}/${counters.techsTotal}`}
                                dotClass="bg-blue-400"
                                /* #828 m1: say it the way a person would. */
                                tip={`${counters.techsOnDuty} of ${counters.techsTotal} techs have work this day.`}
                            />
                        )}
                    </div>
                </TooltipProvider>
            )}

            {newGrid && <NewGridSwitch on={newGrid.on} onChange={newGrid.onChange} />}

            {/* ── Live light (#1465) — right cluster, every view, everyone ── */}
            {live && (
                <div className="ml-auto flex items-center">
                    <LiveChip {...live} />
                </div>
            )}

            {/* ── Automation cluster — dispatch.manage ONLY ────────── */}
            {canAutomate && (
                <div className="flex items-center gap-2 border-l border-shell-foreground/15 pl-2">
                    <AutoScheduleToggle {...autoSchedule} />
                    {mode === "day" && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="default" size="sm" className="h-8">
                                    <Wand2 className="mr-1 h-4 w-4" /> Automation
                                    <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem
                                    onSelect={onAutoFill}
                                    title="Build day-routes from queued unscheduled work"
                                >
                                    <Wand2 className="mr-2 h-4 w-4" /> Auto-fill
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={onSuggestMoves} title="Suggest moves to balance load">
                                    <Scale className="mr-2 h-4 w-4" /> Suggest moves
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            )}
        </div>
    );
}
